/**
 * membership-form.js
 * Handles the Clube Funcional enrollment form submission via
 * the Shopify App Proxy endpoint (/apps/club-membership/enroll).
 *
 * Architecture: Browser → POST /apps/club-membership/enroll → Shopify App → Admin API metafieldsSet
 * The hidden customer_id field is rendered server-side by Liquid (trusted identity).
 */

(function () {
  'use strict';

  // ─── LOGGER ────────────────────────────────────────────────────────────────
  // Prefixes every message so it's easy to filter in DevTools:
  //   Console filter: "[MF]"
  var LOG_PREFIX = '[MF]';

  function log(level, tag, msg, data) {
    var timestamp = new Date().toISOString();
    var fullMsg = LOG_PREFIX + '[' + tag + '] ' + msg;
    if (data !== undefined) {
      console[level](timestamp, fullMsg, data);
    } else {
      console[level](timestamp, fullMsg);
    }
  }

  var logger = {
    info:  function (tag, msg, data) { log('info',  tag, msg, data); },
    warn:  function (tag, msg, data) { log('warn',  tag, msg, data); },
    error: function (tag, msg, data) { log('error', tag, msg, data); },
    debug: function (tag, msg, data) { log('debug', tag, msg, data); },
    group: function (label) { console.group(LOG_PREFIX + ' ' + label); },
    groupEnd: function () { console.groupEnd(); },
  };
  // ───────────────────────────────────────────────────────────────────────────

  // --- CPF mask (requires jquery.mask.min.js) ---
  function initCpfMask() {
    if (window.jQuery) {
      jQuery('[name="cpf"]').mask('000.000.000-00');
      jQuery('[name="birth_date"]').mask('00/00/0000');
      logger.debug('INIT', 'CPF + birth_date masks applied via jQuery.mask');
    } else {
      logger.warn('INIT', 'jQuery not found — CPF/date masks skipped');
    }
  }

  /**
   * Parse a date string in dd/mm/yyyy format.
   * Returns a Date object, or null if invalid.
   */
  function parseBrDate(str) {
    if (!str || str.length !== 10) return null;
    var parts = str.split('/');
    if (parts.length !== 3) return null;
    var day = parseInt(parts[0], 10);
    var month = parseInt(parts[1], 10) - 1; // 0-based
    var year = parseInt(parts[2], 10);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    var d = new Date(year, month, day);
    // Guard against invalid dates like 31/02/2000
    if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
    return d;
  }

  /**
   * Convert dd/mm/yyyy → YYYY-MM-DD (ISO 8601).
   * Returns empty string if input is invalid.
   */
  function brDateToIso(str) {
    var d = parseBrDate(str);
    if (!d) return '';
    var yyyy = d.getFullYear();
    var mm   = String(d.getMonth() + 1).padStart(2, '0');
    var dd   = String(d.getDate()).padStart(2, '0');
    return yyyy + '-' + mm + '-' + dd;
  }

  // --- Age auto-calculation from birth_date ---
  function initAgeCalculation() {
    var birthDateInput = document.querySelector('[name="birth_date"]');
    if (!birthDateInput) {
      logger.warn('INIT', 'birth_date input not found — age calculation skipped');
      return;
    }

    birthDateInput.addEventListener('input', function () {
      var raw = this.value;
      if (raw.length < 10) return; // wait until fully entered
      var birthDate = parseBrDate(raw);
      if (!birthDate) {
        logger.warn('AGE', 'Invalid birth_date value', raw);
        return;
      }
      var today = new Date();
      var age = today.getFullYear() - birthDate.getFullYear();
      var m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      var ageHidden = document.getElementById('age-hidden');
      if (ageHidden) {
        ageHidden.value = age >= 0 ? age : '';
        logger.debug('AGE', 'Calculated age from birth_date', { birthDate: raw, age: age });
      }
    });
  }


  // --- Form validation ---
  function validateForm(form) {
    const errors = [];

    const birthDate = form.querySelector('[name="birth_date"]');
    if (!birthDate || !birthDate.value || birthDate.value.length < 10) {
      errors.push('Data de nascimento é obrigatória.');
    } else if (!parseBrDate(birthDate.value)) {
      errors.push('Data de nascimento inválida. Use o formato dd/mm/aaaa.');
    }

    const cpf = form.querySelector('[name="cpf"]');
    if (!cpf || !cpf.value.trim()) errors.push('CPF é obrigatório.');

    const gender = form.querySelector('[name="gender"]');
    if (!gender || !gender.value) errors.push('Sexo é obrigatório.');

    const skinType = form.querySelector('[name="skin_type"]');
    if (!skinType || !skinType.value) errors.push('Tipo de pele é obrigatório.');

    const terms = form.querySelector('[name="participation_terms"]');
    if (!terms || !terms.checked) errors.push('Você precisa aceitar os termos de participação.');

    if (errors.length > 0) {
      logger.warn('VALIDATE', 'Client-side validation failed', errors);
    } else {
      logger.info('VALIDATE', 'Client-side validation passed');
    }

    return errors;
  }

  // --- Helper: collect checked checkbox values as a JSON array string (for list.single_line_text_field metafields) ---
  function collectCheckboxValues(form, name) {
    var checked = form.querySelectorAll('[name="' + name + '"]:checked');
    var values = Array.prototype.map.call(checked, function (cb) { return cb.value; });
    return JSON.stringify(values);
  }

  // --- Calculate age from dd/mm/yyyy string ---
  function calcAge(brDateStr) {
    var birthDate = parseBrDate(brDateStr);
    if (!birthDate) return 0;
    var today = new Date();
    var age = today.getFullYear() - birthDate.getFullYear();
    var m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age >= 0 ? age : 0;
  }

  // --- Build metafields array ---
  function buildMetafields(form) {
    const allergyValue = (form.querySelector('[name="has_allergy"]')?.value || '').trim();
    const birthDateBr  = form.querySelector('[name="birth_date"]').value; // dd/mm/yyyy
    const birthDateIso = brDateToIso(birthDateBr);                        // YYYY-MM-DD for Admin API
    const age = calcAge(birthDateBr);

    return [
      { namespace: 'custom', key: 'apelido',                                                                      value: form.querySelector('[name="nickname"]')?.value || '',              type: 'single_line_text_field' },
      { namespace: 'facts',  key: 'birth_date',                                                                   value: birthDateIso,                                                     type: 'date' },
      { namespace: 'custom', key: 'cpf',                                                                          value: form.querySelector('[name="cpf"]').value,                         type: 'single_line_text_field' },
      { namespace: 'custom', key: 'sexo',                                                                         value: form.querySelector('[name="gender"]').value,                      type: 'single_line_text_field' },
      { namespace: 'custom', key: 'endereo',                                                                      value: form.querySelector('[name="address"]')?.value || '',              type: 'single_line_text_field' },
      // number_integer — calculated inline at submission time from birth_date
      { namespace: 'custom', key: 'idade',                                                                        value: String(age),                                                     type: 'number_integer' },
      { namespace: 'custom', key: 'tipo_de_pele',                                                                 value: form.querySelector('[name="skin_type"]').value,                   type: 'single_line_text_field' },
      { namespace: 'custom', key: 'caractersticas_adicionais_da_sua_pele',                                        value: collectCheckboxValues(form, 'skin_additional_traits[]'),          type: 'list.single_line_text_field' },
      { namespace: 'custom', key: 'o_que_te_incomoda_hoje_na_sua_pele',                                           value: collectCheckboxValues(form, 'skin_concerns[]'),                   type: 'list.single_line_text_field' },
      { namespace: 'custom', key: 'possui_alguma_alergia_ou_hipersensibilidade',                                  value: allergyValue || 'Nenhuma',                                       type: 'single_line_text_field' },
      // Shopify defines this as single_line_text_field (not list) — collect first selected value
      { namespace: 'custom', key: 'o_que_voc_mais_quer_ter_a_oportunidade_de_ter_acesso_como_membro',             value: (function() { var checked = form.querySelectorAll('[name="club_wish[]"]:checked'); return checked.length > 0 ? checked[0].value : ''; })(), type: 'single_line_text_field' },
      // Shopify defines this as list.single_line_text_field — must be a JSON array
      { namespace: 'custom', key: 'comunicao_e_novidades',                                                        value: (function() { var cb = form.querySelector('[name="communication_consent"]'); return cb && cb.checked ? JSON.stringify([cb.value]) : JSON.stringify([]); })(), type: 'list.single_line_text_field' },
      { namespace: 'custom', key: 'algum_recado_que_gostaria_de_deixar_para_ns_ps_a_ceo_l_todos_3',              value: form.querySelector('[name="customer_message"]')?.value || '',     type: 'single_line_text_field' },
      // termos_de_participao is written LAST — triggers Shopify Flow for tag swap
      // Value must match the exact choice defined in Shopify: "Concordo com os termos de participação com o Regulamento Promocional"
      { namespace: 'custom', key: 'termos_de_participao',                                                         value: 'Concordo com os termos de participação com o Regulamento Promocional', type: 'single_line_text_field' },
    ];
  }

  // --- App Proxy endpoint ---
  var PROXY_URL = '/apps/club-membership/enroll';

  // --- Submit handler ---
  async function handleSubmit(e) {
    e.preventDefault();

    const form = e.target;
    const submitBtn = form.querySelector('[type="submit"]');
    const errorEl = document.getElementById('membership-form-error');

    logger.group('Form Submit');
    logger.info('SUBMIT', 'Form submission started');

    // Reset error state
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    // Client-side validation
    const validationErrors = validateForm(form);
    if (validationErrors.length > 0) {
      if (errorEl) {
        errorEl.textContent = validationErrors.join(' ');
        errorEl.hidden = false;
      }
      logger.groupEnd();
      return;
    }

    // Disable submit while processing
    submitBtn.disabled = true;
    const originalText = submitBtn.textContent;
    submitBtn.textContent = 'Aguarde...';

    try {
      // ── 1. Build payload ─────────────────────────────────────────────────
      const customerId = form.querySelector('[name="customer_id"]').value;
      logger.info('PAYLOAD', 'Customer ID from hidden field', customerId);

      if (!customerId) {
        throw new Error('ID do cliente não encontrado. Por favor, faça login novamente.');
      }

      const metafields = buildMetafields(form);
      logger.debug('PAYLOAD', 'Metafields built', metafields.map(function (mf) {
        return { namespace: mf.namespace, key: mf.key, type: mf.type, valueLength: String(mf.value).length };
      }));

      const requestBody = JSON.stringify({ customer_id: customerId, metafields: metafields });
      logger.debug('PAYLOAD', 'Request body size (bytes)', requestBody.length);

      // ── 2. App Proxy call ────────────────────────────────────────────────
      logger.group('PROXY Request');
      logger.info('PROXY', 'POST ' + PROXY_URL, {
        customer_id:      customerId,
        metafields_count: metafields.length,
      });

      const response = await fetch(PROXY_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    requestBody,
      });

      logger.info('PROXY', 'Response received', {
        status:     response.status,
        statusText: response.statusText,
        ok:         response.ok,
      });

      const data = await response.json();
      logger.debug('PROXY', 'Response body', data);
      logger.groupEnd();

      if (!response.ok || !data.success) {
        const errMsg = Array.isArray(data.errors) && data.errors.length > 0
          ? data.errors.join(', ')
          : 'Erro ao enviar formulário (HTTP ' + response.status + ').';
        logger.error('PROXY', 'Enrollment failed', { status: response.status, errors: data.errors });
        throw new Error(errMsg);
      }

      logger.info('PROXY', 'Metafields written successfully via App Proxy');

      // ── 3. Show success state ─────────────────────────────────────────────
      const formWrapper = document.getElementById('membership-form-wrapper');
      const successBlock = document.getElementById('membership-success');
      if (formWrapper) formWrapper.hidden = true;
      if (successBlock) successBlock.hidden = false;
      logger.info('SUBMIT', 'Submission complete — success state shown');

    } catch (err) {
      logger.error('SUBMIT', 'Submission failed', { message: err.message, stack: err.stack });
      if (errorEl) {
        errorEl.textContent = err.message || 'Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.';
        errorEl.hidden = false;
      }
      submitBtn.disabled = false;
      submitBtn.textContent = originalText;
    } finally {
      logger.groupEnd();
    }
  }

  // --- Init ---
  function init() {
    logger.info('INIT', 'membership-form.js initializing', {
      readyState: document.readyState,
      proxyUrl:   PROXY_URL,
    });

    initCpfMask();
    initAgeCalculation();

    const form = document.getElementById('membership-enrollment-form');
    if (form) {
      form.addEventListener('submit', handleSubmit);
      logger.info('INIT', 'Submit listener attached to #membership-enrollment-form');
    } else {
      logger.warn('INIT', '#membership-enrollment-form not found in DOM — is the user eligible?');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
